import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
} from '@mui/material';
import {
  Api as ApiIcon,
  Code as CodeIcon,
  Description as DocsIcon,
} from '@mui/icons-material';

const ApiDocumentation = () => {
  const { t } = useTranslation();
  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
          {t('api_documentation')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {t('interactive_api_testing')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            height: '100%',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ApiIcon sx={{ color: '#667eea', mr: 1 }} />
                <Typography variant="h6" sx={{ color: 'white' }}>
                  {t('rest_api')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                {t('explore_test_endpoints')}
              </Typography>
              <Chip label={t('available')} color="success" size="small" />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            height: '100%',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CodeIcon sx={{ color: '#f093fb', mr: 1 }} />
                <Typography variant="h6" sx={{ color: 'white' }}>
                  {t('websocket_api')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                {t('realtime_websocket_docs')}
              </Typography>
              <Chip label={t('live')} color="success" size="small" />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            height: '100%',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DocsIcon sx={{ color: '#43e97b', mr: 1 }} />
                <Typography variant="h6" sx={{ color: 'white' }}>
                  {t('openapi_spec')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                {t('download_openapi_spec')}
              </Typography>
              <Chip label={t('version_3_0')} color="info" size="small" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ 
        mt: 4,
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
            {t('api_endpoints')}
          </Typography>
          <Divider sx={{ mb: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label={t('get')} color="success" size="small" />
              <Typography variant="body2" sx={{ color: 'white' }}>
                /health
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('health_check_endpoint')}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label={t('get')} color="success" size="small" />
              <Typography variant="body2" sx={{ color: 'white' }}>
                /metrics/dashboard
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('system_metrics_dashboard')}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label={t('get')} color="success" size="small" />
              <Typography variant="body2" sx={{ color: 'white' }}>
                /metrics/system
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('dashboard_real_time_system_metrics')}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label={t('get')} color="success" size="small" />
              <Typography variant="body2" sx={{ color: 'white' }}>
                /sandbox/status
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('sandbox_environment_status')}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label={t('post')} color="warning" size="small" />
              <Typography variant="body2" sx={{ color: 'white' }}>
                /sandbox/execute
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('execute_code_in_sandbox')}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label={t('post')} color="warning" size="small" />
              <Typography variant="body2" sx={{ color: 'white' }}>
                /benchmark/load-test
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('start_load_testing')}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label={t('get')} color="success" size="small" />
              <Typography variant="body2" sx={{ color: 'white' }}>
                /security/scan
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('security_vulnerability_scan')}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label={t('websocket_short')} color="info" size="small" />
              <Typography variant="body2" sx={{ color: 'white' }}>
                /ws
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('websocket_real_time')}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ApiDocumentation;
